import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Comprehensive filter: browser extensions, cross-origin, Firefox reader, Safari quirks
    const msg = (error?.message || '').toLowerCase();
    const stack = (error?.stack || '').toLowerCase();
    if (
      msg.includes('__firefox__') || msg.includes('firefox') ||
      msg.includes('__crweb') || msg.includes('crweb') ||
      msg.includes('reader') ||
      msg.includes('youtube') ||
      msg.includes('extension') ||
      msg.includes('script error') ||
      msg.includes('resizeobserver') ||
      msg.includes('loading chunk') ||
      msg.includes('loading css chunk') ||
      msg.includes('network error') ||
      stack.includes('extension') ||
      stack.includes('chrome-extension') ||
      stack.includes('moz-extension')
    ) {
      return null; // Don't show error boundary for non-app errors
    }
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const msg = (error?.message || '').toLowerCase();
    const stack = (error?.stack || '').toLowerCase();
    if (
      msg.includes('__firefox__') || msg.includes('firefox') ||
      msg.includes('__crweb') || msg.includes('crweb') ||
      msg.includes('reader') ||
      msg.includes('youtube') ||
      msg.includes('extension') ||
      msg.includes('script error') ||
      msg.includes('resizeobserver') ||
      stack.includes('extension') ||
      stack.includes('chrome-extension') ||
      stack.includes('moz-extension')
    ) {
      // Silently recover — reset error state
      this.setState({ hasError: false, error: null });
      return;
    }
    console.error('App Error:', error, errorInfo);

    // Report to the server so the fault is captured without a console.
    // Fire-and-forget: a failure here must never mask the original error.
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-errors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: error?.message || String(error),
          stack: error?.stack || '',
          componentStack: errorInfo?.componentStack || '',
          url: window.location.href,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) { /* ignore */ }
  }

  render() {
    if (this.state.hasError) {
      // Scoped use: caller supplies an inline fallback so the failure is
      // contained to one panel instead of unmounting the whole app.
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback(
          this.state.error,
          () => this.setState({ hasError: false, error: null }),
        );
      }
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0A0A0A',
          fontFamily: "Georgia, 'Times New Roman', serif",
          padding: '24px',
        }}>
          <div style={{ textAlign: 'center', maxWidth: 420 }}>
            <div style={{
              fontSize: 16,
              fontWeight: 700,
              color: '#E8E0D4',
              letterSpacing: 3,
              textTransform: 'uppercase',
              marginBottom: 24,
            }}>
              REVITALISE KERATIN
            </div>
            <div style={{
              width: 40,
              height: 2,
              background: '#B99A65',
              margin: '0 auto 24px',
            }} />
            <h2 style={{
              fontSize: 22,
              color: '#E8E0D4',
              marginBottom: 12,
              fontWeight: 400,
            }}>
              Something went wrong
            </h2>
            <p style={{
              fontSize: 14,
              color: '#8A8078',
              lineHeight: 1.6,
              marginBottom: 24,
              fontFamily: "Arial, sans-serif",
            }}>
              We apologise for the inconvenience. Please refresh the page to continue.
            </p>
            {this.state.error && (
              <details style={{ textAlign: 'left', marginBottom: 24, color: '#8A8078', fontSize: 11, fontFamily: 'Menlo, Consolas, monospace' }}>
                <summary style={{ cursor: 'pointer', color: '#B99A65' }}>Show technical details</summary>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 240, overflow: 'auto', marginTop: 8 }}>
                  {String(this.state.error?.message || this.state.error)}
                  {'\n\n'}
                  {String(this.state.error?.stack || '').slice(0, 1500)}
                </pre>
              </details>
            )}
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              style={{
                background: '#B99A65',
                color: '#0A0A0A',
                border: 'none',
                padding: '14px 32px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 2,
                textTransform: 'uppercase',
                cursor: 'pointer',
                fontFamily: "Arial, sans-serif",
              }}
              data-testid="error-boundary-refresh"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
